import { suggestBudget } from '@fd/finance-core';
import type { Category, CategoryGroup } from '@fd/schemas';
import type { BudgetsData, BudgetView, Synced } from './data.ts';

// The budget as the owner plans it: main categories, some with subcategories (a group, D-035).
// Amounts are set on a category; a main category with subcategories adds theirs up, and its
// spending is what all of them spent — or it has one limit of its own that all its subcategories
// share (D-047), and then theirs are not set.

export type PlanLeaf = {
  category: Synced<Category>;
  view: BudgetView | null;
  spentMinor: bigint;
  /** The average of the last months, offered where no amount is set yet. */
  suggestedMinor: bigint | null;
};

export type PlanMain = {
  key: string;
  /** Null for a main category without subcategories: its one leaf is the category itself. */
  group: Synced<CategoryGroup> | null;
  leaves: PlanLeaf[];
  /** The main category's own limit, shared by all its subcategories (D-047); null: theirs add up. */
  view: BudgetView | null;
  /** The budgets this main category counts: its own, or its subcategories'. */
  views: BudgetView[];
  limitMinor: bigint;
  spentMinor: bigint;
  /** Steady spending by today, for the tick on the bar. */
  expectedMinor: bigint;
  budgeted: boolean;
};

/** Days a new category stays in the list with nothing set or spent, so it can be given its amount. */
const NEW_DAYS = 30;

/**
 * A main category that waits behind "show more" (as in Monarch): no amount, nothing spent, nothing to
 * suggest — unless it was made in the last month (the owner, 01.10.2026: „nowe kategorie nie mają opcji
 * ustawienia budżetu” — a new one was always hidden there).
 */
export function isIdle(main: PlanMain, today: string): boolean {
  if (main.group || main.budgeted || main.spentMinor !== 0n || main.leaves[0]!.suggestedMinor !== null) return false;
  const made = Date.parse(main.leaves[0]!.category.createdAt);
  return !(made > Date.parse(`${today}T00:00:00Z`) - NEW_DAYS * 86_400_000);
}

export function buildBudgetPlan(data: BudgetsData, categories: readonly Synced<Category>[], groups: readonly Synced<CategoryGroup>[], today: string) {
  const views = new Map<string, BudgetView>();
  const groupViews = new Map<string, BudgetView>();
  for (const v of data.views) {
    if (v.group) groupViews.set(v.group.id, v);
    else if (v.category) views.set(v.category.id, v);
  }
  // Under a limit of the whole main category a subcategory has no amount of its own to suggest.
  const leaf = (category: Synced<Category>, shared = false): PlanLeaf => {
    const spending = data.byCategory.get(category.id) ?? new Map<string, bigint>();
    const view = shared ? null : (views.get(category.id) ?? null);
    return { category, view, spentMinor: spending.get(data.month) ?? 0n, suggestedMinor: view || shared ? null : suggestBudget(spending, today) };
  };
  const sum = (leaves: readonly PlanLeaf[], pick: (v: BudgetView) => bigint) => leaves.reduce((acc, l) => acc + (l.view ? pick(l.view) : 0n), 0n);
  const shown = categories.filter((c) => c.kind === 'EXPENSE' && c.archivedAt === null);
  const live = new Set(groups.map((g) => g.id));
  const mains: PlanMain[] = [];
  for (const group of groups) {
    const members = categories.filter((c) => c.groupId === group.id);
    // A group of income categories only is not a spending one; an empty one waits for its first subcategory.
    if (members.length > 0 && !members.some((c) => c.kind === 'EXPENSE')) continue;
    const own = groupViews.get(group.id) ?? null;
    const leaves = members.filter((c) => shown.includes(c)).map((c) => leaf(c, own !== null));
    mains.push({
      key: `g:${group.id}`,
      group,
      leaves,
      view: own,
      views: own ? [own] : leaves.flatMap((l) => (l.view ? [l.view] : [])),
      limitMinor: own ? own.status.limitMinor : sum(leaves, (v) => v.status.limitMinor),
      spentMinor: data.byGroup.get(group.id)?.get(data.month) ?? 0n,
      expectedMinor: own ? own.status.expectedMinor : sum(leaves, (v) => v.status.expectedMinor),
      budgeted: own !== null || leaves.some((l) => l.view !== null),
    });
  }
  for (const category of shown.filter((c) => !c.groupId || !live.has(c.groupId))) {
    const one = leaf(category);
    mains.push({
      key: category.id,
      group: null,
      leaves: [one],
      view: null,
      views: one.view ? [one.view] : [],
      limitMinor: one.view?.status.limitMinor ?? 0n,
      spentMinor: one.spentMinor,
      expectedMinor: one.view?.status.expectedMinor ?? 0n,
      budgeted: one.view !== null,
    });
  }
  // Spending outside every amount: a category without one, unless its main category's limit covers it.
  const uncovered = mains.flatMap((m) => (m.view ? [] : m.leaves.filter((l) => !l.view)));
  return {
    mains,
    /** Spent this month in categories without an amount, and with no category at all. */
    outsideMinor: uncovered.reduce((acc, l) => acc + l.spentMinor, 0n) + (data.byCategory.get(null)?.get(data.month) ?? 0n),
    uncategorizedMinor: data.byCategory.get(null)?.get(data.month) ?? 0n,
  };
}

/** The month's budget as the cash-flow diagram shows it: the total, and each limit by its category or group. */
export type FlowBudget = {
  limitMinor: bigint;
  spentMinor: bigint;
  /** Steady spending by today, for the tick on the bar. */
  expectedMinor: bigint;
  daysLeft: number;
  current: boolean;
  /** A limit by `g:<group id>` or a category's id (also for a subcategory with an amount of its own). */
  limits: ReadonlyMap<string, bigint>;
};

export function flowBudget(plan: ReturnType<typeof buildBudgetPlan>, data: BudgetsData, today: string): FlowBudget {
  const mains = plan.mains.filter((m) => m.budgeted && m.limitMinor > 0n);
  const views = mains.flatMap((m) => m.views);
  const sum = (pick: (v: BudgetView) => bigint) => views.reduce((acc, v) => acc + pick(v), 0n);
  const limits = new Map<string, bigint>();
  for (const m of mains) {
    limits.set(m.key, m.limitMinor);
    for (const l of m.leaves) if (l.view && l.view.status.limitMinor > 0n) limits.set(l.category.id, l.view.status.limitMinor);
  }
  return {
    limitMinor: sum((v) => v.status.limitMinor),
    spentMinor: sum((v) => v.status.spentMinor),
    expectedMinor: sum((v) => v.status.expectedMinor),
    daysLeft: views[0]?.status.daysLeft ?? 0,
    current: data.month === `${today.slice(0, 8)}01`,
    limits,
  };
}
